import type { MouseEvent, ReactNode } from "react";
import { Icon } from "@circleco/compass/components/Icon";
import { Link } from "@circleco/compass/components/Link";
import { Tooltip } from "@circleco/compass/components/Tooltip";
import { useCommunitySwitcherContext } from "../context/CommunitySwitcherContext";
import type { CommunitySwitcher } from "../types/CommunitySwitcher";
import { cn } from "../helpers/cn";
import { getCommunitySwitcherIconUrl } from "../helpers/communitySwitcherItem";
import { CommunitiesModalLinkIconAndName } from "./CommunitiesModalLinkIconAndName";
import { CommunitiesModalLinkStaticDragControl } from "./CommunitiesModalLinkStaticDragControl";

export interface CommunitiesModalLinkProps {
  readonly community: CommunitySwitcher;
  readonly dragHandle?: ReactNode;
  readonly href: string;
  readonly isDragDisabled?: boolean;
  readonly notificationCount: number;
  readonly splitRowLayout?: boolean;
}

const LINK_ROW_BASE =
  "hover:bg-tertiary flex items-center justify-between gap-x-1 rounded-lg p-1 transition-colors duration-75";

export const CommunitiesModalLink = ({
  community,
  dragHandle,
  href,
  isDragDisabled = false,
  notificationCount,
  splitRowLayout = false,
}: CommunitiesModalLinkProps) => {
  const { featureFlags, theme, t } = useCommunitySwitcherContext();
  const iconUrl = getCommunitySwitcherIconUrl(
    community,
    theme.isDarkAppearance,
  );

  const communityLinkAriaLabel =
    notificationCount > 0
      ? `${community.name}, ${t("community_sidebar.unread_notifications", { count: notificationCount })}`
      : undefined;

  const iconAndName = (
    <CommunitiesModalLinkIconAndName
      community={community}
      iconUrl={iconUrl}
      notificationCount={notificationCount}
    />
  );

  const handleCommunitySwitch = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      community.is_current_community ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    featureFlags?.onCommunitySwitch?.();
  };

  if (dragHandle || splitRowLayout) {
    return (
      <div className={cn(LINK_ROW_BASE, "focus-within:bg-tertiary")}>
        {dragHandle ?? (
          <CommunitiesModalLinkStaticDragControl
            isDragDisabled={isDragDisabled}
          />
        )}
        <a
          aria-label={communityLinkAriaLabel}
          className="flex min-w-0 flex-1 items-center gap-3"
          data-community-switcher-nav=""
          href={href}
          onClick={handleCommunitySwitch}
        >
          {iconAndName}
        </a>
      </div>
    );
  }

  return (
    <div className={cn(LINK_ROW_BASE, "focus-within:bg-tertiary")}>
      <span className="inline-flex shrink-0" aria-hidden>
        <Tooltip
          content={t("community_switcher_drag_handle")}
          delay={200}
          side="top"
        >
          <span
            className={cn("text-secondary inline-flex p-1", {
              "cursor-grab": !isDragDisabled,
              "cursor-not-allowed": isDragDisabled,
            })}
          >
            <Icon name="drag" size="sm" />
          </span>
        </Tooltip>
      </span>
      <Link
        aria-label={communityLinkAriaLabel}
        className="flex min-w-0 flex-1 items-center gap-3"
        data-community-switcher-nav=""
        href={href}
        onClick={handleCommunitySwitch}
      >
        {iconAndName}
      </Link>
    </div>
  );
};
